/**
 * The hosted BunnyQuery widget page: read the version a project is serving, and
 * rewrite that page to the current one.
 *
 * A BunnyQuery project serves its chat at `<subdomain>.<hostDomain>/index.html`.
 * That page is rendered from `infra/bq-template.html`, whose UNRENDERED copy is
 * published to the storage CDN as `bq/widget/latest/template.html` by
 * `infra/_bunnyquery_widget_update.py`. The same key is what
 * `admin/admin/create_service` downloads when it builds the page for a brand new
 * project, so reading it here puts the dashboard's Update Widget action on
 * exactly the file new projects are born with, rather than inventing a second
 * source of truth that could drift from it.
 *
 * Three things about that arrangement drive the code below:
 *
 *  1. The template carries two fill-in slots and nothing else that is
 *     per-project: `const __PROJECT_ID__ = ...;` and `const __ETC__ = ...;`.
 *     They are matched by NAME with the value left open (the same anchored
 *     regexes the python uses) so an ALREADY-RENDERED page can be re-rendered
 *     without accumulating edits.
 *
 *  2. The widget version is not stored anywhere per service. Neither the service
 *     record (service-settings, service_opt and conf_service are all strict
 *     allowlists) nor the host directory listing (`{name, path, size, upl, cnt}`)
 *     has a field for it. So "which version is hosted" can only be answered by
 *     reading the hosted HTML and scraping the jsdelivr pin, which is what the
 *     python does too.
 *
 *  3. Reading it over the public host URL is unreliable: the edge stamps every
 *     host response `s-maxage=86400`, the cache policy whitelists only `?v` (and
 *     re-interprets it as an S3 version id, so it is not a cache buster), and the
 *     host distribution does not answer OPTIONS, which rules out sending a
 *     `Cache-Control` request header. We presign a direct S3 GET instead, which
 *     bypasses CloudFront entirely and always sees the true current object.
 *
 *  4. Every write of index.html made here first saves the page it is about to
 *     replace as `WIDGET_BACKUP_FILE`, in the same folder. That one file is
 *     what Revert puts back. It is a single level on purpose: the page that was
 *     live before the last change is the one known to have worked, and a longer
 *     history would need a listing and a picker for a job that is "undo that".
 *     infra/_bunnyquery_widget_update.py keeps the same file under the same
 *     name when it sweeps, so a page rewritten by either route can be reverted
 *     from the dashboard.
 */

import { hostDomain, storageCdnDomain, widgetEtcLiteral } from '@/code/service';

/** Where the unrendered template is published. Mirrors the python constants. */
const TEMPLATE_PATH = 'bq/widget/latest/template.html';

/** The hosted page's key inside the project's subdomain folder. */
export const WIDGET_INDEX_FILE = 'index.html';

/**
 * Where the page being replaced is kept, beside index.html. Mirrors
 * `BACKUP_FILE` in infra/_bunnyquery_widget_update.py.
 *
 * The double underscores are the house mark for a system-managed name (the
 * same one `isReservedDbEntry` reads), so it cannot be mistaken for a page the
 * owner wrote and will not collide with one. It keeps the .html extension so
 * the object is stored with the right Content-Type and can be opened as a page
 * when someone wants to look at what Revert would restore.
 */
export const WIDGET_BACKUP_FILE = '__widget_backup__.html';

/**
 * The widget build a page loads, scraped from its jsdelivr pins. Same pattern as
 * `WIDGET_VERSION_RE` in infra/_bunnyquery_widget_update.py, and deliberately
 * so: if the two disagree about what "the hosted version" is, the dashboard and
 * the sweep script would report different numbers for the same file.
 */
const WIDGET_VERSION_RE = /bunnyquery@([0-9][0-9A-Za-z.+-]*)\//;

/**
 * What makes a hosted index.html a BunnyQuery chat page at all, as opposed to a
 * site the owner published through web hosting: it loads the widget build from
 * the CDN, or it calls BunnyQuery.init (a page that pins `@latest` has no version
 * to scrape, so the version alone cannot tell the two apart). The update replaces
 * index.html, and the Settings row asks first when this says no.
 */
const WIDGET_PAGE_RE = /cdn\.jsdelivr\.net\/npm\/bunnyquery@|BunnyQuery\.init\s*\(/;

/**
 * Cap on any single read here.
 *
 * This exists so the Settings row can DISABLE its button while the check runs:
 * a probe with no ceiling can hang forever behind a stalled socket, and gating
 * the button on an unbounded read is how you end up with a control that never
 * comes back. Every fetch in this module is bounded, so the probe always
 * settles and the gate is always released.
 */
const FETCH_TIMEOUT_MS = 8000;

/**
 * The two const declarations the template carries. Matched by NAME with the
 * value left open, so the template can ship with any placeholder ("" / null) and
 * a rendered page can be re-rendered without stacking edits.
 */
const PROJECT_ID_DECL = /^([ \t]*const[ \t]+__PROJECT_ID__[ \t]*=[ \t]*).*?(;[ \t]*)$/m;
const ETC_DECL = /^([ \t]*const[ \t]+__ETC__[ \t]*=[ \t]*).*?(;[ \t]*)$/m;
/**
 * Which MCP server the hosted page talks to. OPTIONAL, unlike the two above: a
 * template published before this const existed has no `__DEV__` line, and Update
 * Widget must still work against it. Such a page falls back to its own hostname
 * (dev hosting is *.skapi.app), which resolves correctly on its own.
 */
const DEV_DECL = /^([ \t]*const[ \t]+__DEV__[ \t]*=[ \t]*).*?(;[ \t]*)$/m;
/**
 * `null` rather than `false` on prod, matching the infra renderers: the template
 * reads `__DEV__ === true || <hostname is skapi.app>`, so null leaves the
 * hostname check as the deciding signal instead of overriding it.
 */
const DEV_LITERAL = hostDomain === 'skapi.app' ? 'true' : 'null';

/** Cap on what we will treat as a widget page. The python uses the same 256KB. */
const MAX_TEMPLATE_BYTES = 256 * 1024;

/**
 * `fetch` with a ceiling. Rejects the same way a network failure does, so every
 * caller's existing catch already handles it.
 */
async function fetchWithTimeout(url: string, init?: RequestInit): Promise<Response> {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), FETCH_TIMEOUT_MS);
    try {
        return await fetch(url, { ...(init || {}), signal: ctl.signal });
    } finally {
        clearTimeout(timer);
    }
}

export class WidgetUpdateError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'WidgetUpdateError';
    }
}

/** The widget version a page pins, or null when it pins none. */
export function parseWidgetVersion(html: string): string | null {
    const m = html ? html.match(WIDGET_VERSION_RE) : null;
    return m ? m[1] : null;
}

/** Whether a page is a BunnyQuery chat page (see WIDGET_PAGE_RE). */
export function isWidgetPage(html: string): boolean {
    return !!html && WIDGET_PAGE_RE.test(html);
}

/**
 * Fetch the published template. Unauthenticated and CORS-open (the storage
 * distribution answers `Access-Control-Allow-Origin: *`), so this is a plain
 * fetch with no skapi involvement.
 *
 * A 403 here is the normal shape of "nothing has been published for this
 * environment yet" (S3 returns AccessDenied, not NoSuchKey, for a missing object
 * the caller may not list), so it gets its own message: the fix is to run the
 * publish step of the infra script, not to retry.
 */
export async function fetchLatestWidgetTemplate(): Promise<{ html: string; version: string | null }> {
    const url = `${storageCdnDomain}/${TEMPLATE_PATH}`;

    let res: Response;
    try {
        // `no-store`, because the alias ships `public, max-age=300`: without it a
        // dashboard reopened inside that window reads the browser's own copy and
        // reports a template that has already been replaced. It is a fetch
        // option, not a request header, so it adds no CORS preflight.
        res = await fetchWithTimeout(url, { cache: 'no-store' });
    } catch (err: any) {
        throw new WidgetUpdateError(
            'Could not reach the widget template. Check your connection and try again.',
        );
    }

    if (res.status === 403 || res.status === 404) {
        throw new WidgetUpdateError(
            'No widget template has been published for this environment yet.',
        );
    }
    if (!res.ok) {
        throw new WidgetUpdateError(`Could not read the widget template (HTTP ${res.status}).`);
    }

    const html = await res.text();
    if (!html || html.length > MAX_TEMPLATE_BYTES) {
        throw new WidgetUpdateError('The published widget template looks wrong. Nothing was changed.');
    }
    // Refuse a template we cannot fill in, rather than uploading a page whose
    // project id would stay empty and whose chat would never connect. Same
    // refusal create_service makes before it writes a new project's page.
    if (!PROJECT_ID_DECL.test(html) || !ETC_DECL.test(html)) {
        throw new WidgetUpdateError(
            'The published widget template is missing its project placeholders. Nothing was changed.',
        );
    }

    return { html, version: parseWidgetVersion(html) };
}

/**
 * Fill the template's two slots for one project. `$` is escaped in the injected
 * values because String.replace reads `$1`, `$&` and friends in the REPLACEMENT
 * string; a project id is base64-ish and an etc literal is JSON, so neither is
 * guaranteed to be `$`-free.
 */
export function renderWidgetTemplate(html: string, projectId: string): string {
    if (!projectId) throw new WidgetUpdateError('This project has no project id yet.');

    const lit = (v: string) => v.replace(/\$/g, '$$$$');

    return html
        .replace(PROJECT_ID_DECL, `$1${lit(JSON.stringify(projectId))}$2`)
        .replace(ETC_DECL, `$1${lit(widgetEtcLiteral)}$2`)
        .replace(DEV_DECL, `$1${DEV_LITERAL}$2`);
}

/** One page as it sits in the project's host folder. */
type HostedPage = {
    exists: boolean;
    /** The object exactly as stored. Restoring a page writes these back. */
    bytes: Uint8Array;
    /** The same bytes as text, for scraping the version only. */
    html: string;
};

const NO_PAGE: HostedPage = { exists: false, bytes: new Uint8Array(0), html: '' };

/** A subdomain in transit carries a status flag; nothing is served behind one. */
function settledSubdomain(service: any): string {
    const sd = service?.service?.subdomain;
    return !sd || '+*!'.includes(sd[0]) ? '' : sd;
}

/**
 * Cheap content identity, to answer "are these two pages the same file".
 *
 * FNV-1a rather than crypto.subtle, which only exists in a secure context and
 * so is missing on a dev server reached over plain http. Nothing here is a
 * security decision: a collision would at worst grey out Revert for a page that
 * could have been reverted. The length is part of the value to make even that
 * less likely.
 */
export function pageFingerprint(bytes: Uint8Array): string {
    let h = 0x811c9dc5;
    for (let i = 0; i < bytes.length; i++) {
        h ^= bytes[i];
        h = Math.imul(h, 0x01000193);
    }
    return `${bytes.length}:${(h >>> 0).toString(16)}`;
}

/**
 * Read one file out of the project's host folder, straight from the bucket.
 *
 * `what` names the file in the two messages a reader can see ("the hosted
 * page", "the saved previous page"), so a failure says which read failed.
 */
async function readHostFile(service: any, path: string, what: string): Promise<HostedPage> {
    if (!settledSubdomain(service)) return NO_PAGE;

    let signed: { url: string };
    try {
        signed = await service.getTemporaryUrl({
            request: 'get-host',
            path,
            expires: 60,
        });
    } catch (err: any) {
        // "I could not check" is not "there is no page". An expired session, a
        // throttled admin API or a network blip all land here, and reporting any
        // of them as the definitive "No page published yet" tells the owner of a
        // perfectly good widget that it was never published. Raise it so the row
        // shows the reason instead.
        throw new WidgetUpdateError(
            `Could not check ${what}. ` + (errText(err) || 'Try again in a moment.'),
        );
    }

    let res: Response;
    try {
        res = await fetchWithTimeout(signed.url);
    } catch (err: any) {
        throw new WidgetUpdateError(`Could not read ${what}. Check your connection.`);
    }

    // 403/404 from the presigned GET is a missing object, which is the same
    // "no page hosted" state as a failed presign.
    if (res.status === 403 || res.status === 404) return NO_PAGE;
    if (!res.ok) throw new WidgetUpdateError(`Could not read ${what} (HTTP ${res.status}).`);

    let bytes: Uint8Array;
    try {
        bytes = new Uint8Array(await res.arrayBuffer());
    } catch (err: any) {
        throw new WidgetUpdateError(`Could not read ${what}. Check your connection.`);
    }

    return { exists: true, bytes, html: new TextDecoder('utf-8').decode(bytes) };
}

/**
 * Write one file into the project's host folder. Throws when it did not land.
 *
 * uploadHostFiles takes a FormData/form and reads File entries off it; the
 * destination key is built server side as `<subdomain>/<file name>`. Same
 * synthetic-form shape hosting.vue's 404-page upload uses.
 */
async function writeHostFile(
    service: any,
    path: string,
    body: string | Uint8Array,
    failed: string,
): Promise<void> {
    const type = 'text/html; charset=utf-8';
    const file = new File([body as BlobPart], path, { type });
    const form = new FormData();
    form.append('file', file);

    let up: any;
    try {
        up = await service.uploadHostFiles(form, {
            target: 'host',
            contentTypeMapping: { [path]: type },
        });
    } catch (err: any) {
        // The presign itself was refused (expired session, storage limit
        // reached), before any byte was sent.
        throw new WidgetUpdateError(failed + (errText(err) ? ' ' + errText(err) : ''));
    }

    if (!up?.completed?.length) {
        const cause = up?.failedErrors?.[0];
        throw new WidgetUpdateError(failed + (cause && errText(cause) ? ' ' + errText(cause) : ''));
    }
}

/** Queue the CDN invalidation. Reports whether it queued, never throws. */
async function refreshHostedPage(service: any): Promise<boolean> {
    try {
        await service.refreshCDN();
        return true;
    } catch (err: any) {
        return false;
    }
}

/** What the Settings row knows about one page. */
export type WidgetPageState = {
    version: string | null;
    fingerprint: string;
    /** false = the file is there but it is not a chat page (a site published through web hosting) */
    widget: boolean;
};

function pageState(bytes: Uint8Array, html: string): WidgetPageState {
    return { version: parseWidgetVersion(html), fingerprint: pageFingerprint(bytes), widget: isWidgetPage(html) };
}

/**
 * Read the project's currently hosted page.
 *
 * Returns `{ hosted: false }` when the project has no index.html rather than
 * throwing: a project whose page was never built (or was deleted) is a normal
 * state the Settings row has to render, not an error.
 *
 * `service` is a `Service` instance from code/service.ts; typed loosely to keep
 * this module free of a circular import back into that file.
 */
export async function readHostedWidget(
    service: any,
): Promise<{ hosted: boolean; version: string | null; fingerprint: string | null; widget: boolean | null }> {
    const page = await readHostFile(service, WIDGET_INDEX_FILE, 'the hosted page');
    if (!page.exists) return { hosted: false, version: null, fingerprint: null, widget: null };
    return { hosted: true, ...pageState(page.bytes, page.html) };
}

/**
 * Read the page Revert would restore.
 *
 * `{ saved: false }` is the normal state of a project that has never been
 * updated from here, so it is a value and not an error. An EMPTY backup object
 * counts as none: restoring it would publish a blank page.
 */
export async function readWidgetBackup(
    service: any,
): Promise<{ saved: boolean; version: string | null; fingerprint: string | null }> {
    const page = await readHostFile(service, WIDGET_BACKUP_FILE, 'the saved previous page');
    if (!page.exists || !page.bytes.length) return { saved: false, version: null, fingerprint: null };
    return { saved: true, ...pageState(page.bytes, page.html) };
}

/** What an update or a revert left behind, for the Settings row to show. */
export type WidgetWriteResult = {
    /** The version now published. */
    version: string | null;
    refreshed: boolean;
    hosted: WidgetPageState;
    /**
     * The page now held as the backup. `null` means this write did not touch
     * the backup file, so whatever the row already knew about it still stands.
     */
    backup: WidgetPageState | null;
};

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}

/**
 * Save the page being replaced, write the rendered template over the project's
 * index.html, then ask for a CDN invalidation.
 *
 * The backup is written FIRST and the update does not go ahead without it: a
 * page that was replaced with nothing kept is exactly the case Revert exists
 * for, so "could not save it" has to mean "did not replace it". Two cases write
 * no backup, and both are correct rather than skipped:
 *
 *  - the project has no page yet, so there is nothing to keep;
 *  - the page already IS what would be written. Saving it would overwrite the
 *    real previous version with a copy of the current one, which is how a
 *    second click on Update would otherwise destroy the only thing worth
 *    reverting to.
 *
 * The refresh is NOT optional. The host distribution serves an object for up to
 * 24h (`s-maxage=86400`), and the automatic invalidation on the hostdir stream
 * only fires when a file's SIZE changes, so a same-size rewrite would otherwise
 * never reach a visitor. Its failure is reported but does not fail the update:
 * the new bytes are already in the bucket at that point, and a refresh can be
 * retried from the Hosting page.
 */
export async function updateHostedWidget(service: any): Promise<WidgetWriteResult> {
    if (!settledSubdomain(service)) {
        throw new WidgetUpdateError('This project has no subdomain to publish to yet.');
    }

    // The published template is the ONLY source of the version, deliberately: the
    // page written here has to be the same file create_service builds a new
    // project from, and it has to be recognisable as such afterwards. Resolving
    // a newer build from npm and rewriting the pin would publish something no
    // template ever contained, and would hide the one fact this row is read for
    // -- whether the template deploy has actually been run.
    const { html, version } = await fetchLatestWidgetTemplate();
    const rendered = renderWidgetTemplate(html, service.service.projectId);
    const renderedBytes = new TextEncoder().encode(rendered);

    // Throws when the page could not be CHECKED, which stops the update: going
    // ahead would replace a page nobody managed to read, with no copy kept.
    const current = await readHostFile(service, WIDGET_INDEX_FILE, 'the hosted page');

    let backup: WidgetPageState | null = null;
    if (current.exists && current.bytes.length && !sameBytes(current.bytes, renderedBytes)) {
        await writeHostFile(
            service,
            WIDGET_BACKUP_FILE,
            current.bytes,
            'The current page could not be saved as a backup, so nothing was changed.',
        );
        backup = pageState(current.bytes, current.html);
    }

    await writeHostFile(service, WIDGET_INDEX_FILE, renderedBytes, 'The page could not be uploaded.');

    return {
        version,
        refreshed: await refreshHostedPage(service),
        hosted: pageState(renderedBytes, rendered),
        backup,
    };
}

/**
 * Put the saved previous page back as index.html.
 *
 * The page it replaces is kept as the new backup, so a revert is itself
 * revertible and nothing is ever the only copy of itself. The order matters:
 * index.html is written first, from bytes already in memory, so a failure at
 * either step leaves a working page published.
 *
 *  - restore fails: nothing changed, the backup is still the previous page;
 *  - restore lands, keeping the replaced page fails: the revert still happened,
 *    and only the page that was just reverted AWAY from is lost. Update brings
 *    the latest one back, so that is the cheap side to lose.
 *
 * The bytes are written back exactly as stored, never re-rendered: the page
 * was working as it stood, and re-filling its slots would turn "what was there"
 * into "what the dashboard thinks was there".
 */
export async function revertHostedWidget(service: any): Promise<WidgetWriteResult> {
    if (!settledSubdomain(service)) {
        throw new WidgetUpdateError('This project has no subdomain to publish to yet.');
    }

    const [saved, current] = await Promise.all([
        readHostFile(service, WIDGET_BACKUP_FILE, 'the saved previous page'),
        readHostFile(service, WIDGET_INDEX_FILE, 'the hosted page'),
    ]);

    if (!saved.exists || !saved.bytes.length) {
        throw new WidgetUpdateError('There is no previous page saved for this project.');
    }
    if (current.exists && sameBytes(current.bytes, saved.bytes)) {
        throw new WidgetUpdateError(
            'The saved previous page is the same as the published one. Nothing was changed.',
        );
    }

    await writeHostFile(
        service,
        WIDGET_INDEX_FILE,
        saved.bytes,
        'The previous page could not be restored. Nothing was changed.',
    );

    // From here the revert has happened. What is left is bookkeeping, and its
    // failure must not be reported as the revert failing.
    let backup = pageState(saved.bytes, saved.html);
    if (current.exists && current.bytes.length) {
        try {
            await writeHostFile(service, WIDGET_BACKUP_FILE, current.bytes, '');
            backup = pageState(current.bytes, current.html);
        } catch (err: any) {
            // The backup file still holds the page that is now published.
        }
    }

    return {
        version: parseWidgetVersion(saved.html),
        refreshed: await refreshHostedPage(service),
        hosted: pageState(saved.bytes, saved.html),
        backup,
    };
}

/** Best-effort one-liner out of whatever a call rejected with. */
function errText(err: any): string {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (typeof err?.message === 'string') return err.message;
    return '';
}

/** Best-effort one-liner out of whatever the S3 POST rejected with. */
